/**
 * Admin Disputes Hook
 * Fetch paginated disputes with filters, stats, detail modal state, and admin actions
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface AdminDispute {
  id: string;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  stripeDisputeId: string;
  stripeChargeId: string;
  amountCents: number;
  reasonCode: string;
  status: 'open' | 'won' | 'lost';
  createdAt: string;
  resolvedAt: string | null;
  disputeBanned: boolean;
}

export interface AdminDisputeDetail extends AdminDispute {
  userRole: string;
  subscription: {
    status: string;
    stripeSubscriptionId: string | null;
    stripePriceId: string | null;
  } | null;
  totalUserDisputes: number;
}

export interface DisputeStats {
  openCount: number;
  wonCount: number;
  lostCount: number;
  totalLostCents: number;
  bannedUsersCount: number;
}

export interface UseAdminDisputesParams {
  status: string;
  page: number;
  limit: number;
}

export interface UseAdminDisputesResult {
  disputes: AdminDispute[];
  total: number;
  pages: number;
  loading: boolean;
  error: string | null;
  stats: DisputeStats | null;
  statsLoading: boolean;
  // Modal state — separated from data to allow loading spinner inside modal
  isDetailModalOpen: boolean;
  selectedDispute: AdminDisputeDetail | null;
  detailLoading: boolean;
  openDisputeDetail: (disputeId: string) => Promise<void>;
  closeDisputeDetail: () => void;
  // Actions
  resolveDispute: (
    disputeId: string,
    status: 'won' | 'lost',
    notes: string,
  ) => Promise<{ success: boolean; error?: string }>;
  unbanUser: (disputeId: string) => Promise<{ success: boolean; error?: string }>;
  reactivateSubscription: (disputeId: string) => Promise<{ success: boolean; error?: string }>;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAdminDisputes(params: UseAdminDisputesParams): UseAdminDisputesResult {
  const { authFetch } = useAuth();
  const [disputes, setDisputes] = useState<AdminDispute[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stats (aggregate, not page-limited)
  const [stats, setStats] = useState<DisputeStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Detail modal state (Issue 3: separate "modal open" from "data loaded")
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState<AdminDisputeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch dispute list
  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
        ...(params.status && params.status !== 'all' && { status: params.status }),
      });

      const response = await authFetch(apiUrl(`/api/admin/disputes?${queryParams}`));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch disputes');
      }

      const data = await response.json();
      setDisputes(data.disputes);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch, params.page, params.limit, params.status]);

  // Fetch aggregate stats (Issue 1: not page-limited)
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await authFetch(apiUrl('/api/admin/disputes/stats'));
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch {
      // Stats are supplementary, don't block UI on failure
    } finally {
      setStatsLoading(false);
    }
  }, [authFetch]);

  // Fetch dispute detail
  const fetchDisputeDetail = useCallback(
    async (disputeId: string) => {
      setDetailLoading(true);
      try {
        const response = await authFetch(apiUrl(`/api/admin/disputes/${disputeId}`));
        if (!response.ok) throw new Error('Failed to fetch dispute detail');
        const data = await response.json();
        setSelectedDispute(data);
      } catch (_err) {
        setSelectedDispute(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [authFetch],
  );

  // Issue 3: Open modal immediately, then fetch data
  const openDisputeDetail = useCallback(
    async (disputeId: string) => {
      setIsDetailModalOpen(true);
      setSelectedDispute(null);
      await fetchDisputeDetail(disputeId);
    },
    [fetchDisputeDetail],
  );

  const closeDisputeDetail = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedDispute(null);
  }, []);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Issue 2: After actions, refresh both list and detail (if modal is open)
  const refreshAfterAction = useCallback(
    async (disputeId?: string) => {
      await Promise.all([fetchDisputes(), fetchStats()]);
      if (isDetailModalOpen && disputeId) {
        await fetchDisputeDetail(disputeId);
      }
    },
    [fetchDisputes, fetchStats, isDetailModalOpen, fetchDisputeDetail],
  );

  const resolveDispute = useCallback(
    async (
      disputeId: string,
      status: 'won' | 'lost',
      notes: string,
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await authFetch(apiUrl(`/api/admin/disputes/${disputeId}/resolve`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status, notes }),
        });

        if (!response.ok) {
          const data = await response.json();
          return { success: false, error: data.error || 'Resolve failed' };
        }

        await refreshAfterAction(disputeId);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
      }
    },
    [authFetch, refreshAfterAction],
  );

  const unbanUser = useCallback(
    async (disputeId: string): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await authFetch(apiUrl(`/api/admin/disputes/${disputeId}/unban`), {
          method: 'POST',
        });

        if (!response.ok) {
          const data = await response.json();
          return { success: false, error: data.error || 'Unban failed' };
        }

        await refreshAfterAction(disputeId);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
      }
    },
    [authFetch, refreshAfterAction],
  );

  const reactivateSubscription = useCallback(
    async (disputeId: string): Promise<{ success: boolean; error?: string }> => {
      try {
        const response = await authFetch(apiUrl(`/api/admin/disputes/${disputeId}/reactivate`), {
          method: 'POST',
        });

        if (!response.ok) {
          const data = await response.json();
          return { success: false, error: data.error || 'Reactivation failed' };
        }

        await refreshAfterAction(disputeId);
        return { success: true };
      } catch (err) {
        return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
      }
    },
    [authFetch, refreshAfterAction],
  );

  return {
    disputes,
    total,
    pages,
    loading,
    error,
    stats,
    statsLoading,
    isDetailModalOpen,
    selectedDispute,
    detailLoading,
    openDisputeDetail,
    closeDisputeDetail,
    resolveDispute,
    unbanUser,
    reactivateSubscription,
    refetch: fetchDisputes,
  };
}
